/* eslint-disable import/prefer-default-export */

export const isNotMatched = (matches, entity, cwfEntities) =>
  matches.findIndex(
    m =>
      m.vendorEntityId === entity.externalId &&
      cwfEntities?.find(cwfEntity => cwfEntity.id === m.cwfEntityId)
  ) === -1;

export const isProductNotMatched = (matches, entity) =>
  matches.findIndex(
    m =>
      m.vendorEntityId === entity.retailerId ||
      m.vendorEntityId === entity.vendorId
  ) === -1;

export const findProductMatch = (matches, entity) =>
  matches.find(
    m =>
      m.vendorEntityId === entity.retailerId ||
      m.vendorEntityId === entity.vendorId
  );

export const getProductsByVendorIdOrRetailerId = (
  invoices,
  vendorOrRetailerId
) =>
  invoices?.flatMap(invoice =>
    invoice.products?.filter(
      product =>
        product.vendorId === vendorOrRetailerId ||
        product.retailerId === vendorOrRetailerId
    )
  );

export const getProductsToPropagateUnitChange = (
  unit,
  invoices,
  productId,
  unitsMatches,
  vendorOrRetailerId
) => {
  if (!unit) {
    return [];
  }

  const sameProductUnMatchedInOtherInvoices = getProductsByVendorIdOrRetailerId(
    invoices,
    vendorOrRetailerId
  )?.filter(product => product.id !== productId && !product.unitMatched);

  if (!sameProductUnMatchedInOtherInvoices?.length) {
    return [];
  }

  // check products in units matches, if have one, it should avoid propagate
  const productsIds = sameProductUnMatchedInOtherInvoices.map(p => p.id);
  const exist = unitsMatches?.find(match =>
    match.productsUnits?.some(
      p => p.id !== productId && productsIds?.includes(p.id)
    )
  );

  if (exist) {
    return [];
  }

  return productsIds;
};

export const findProductByCwfId = (cwfProducts, cwfId) =>
  cwfProducts?.find(
    cwfProduct =>
      cwfProduct?.id === cwfId || cwfProduct?.id === cwfId?.replaceAll('-', '')
  );

export const sortInvoices = invoices => {
  if (!invoices?.length) {
    return invoices;
  }
  const sortedByNumber = invoices.sort((a, b) => {
    const comparison = a?.invoiceNumber?.localeCompare(
      b?.invoiceNumber,
      undefined,
      {
        numeric: true,
        sensitivity: 'base'
      }
    );
    if (comparison !== 0) {
      return comparison;
    }
    return 0;
  });

  const sortedByUnMatched = sortedByNumber.sort((a, b) => {
    const aHaveUnMatched = a.products?.some(product => !product.unitMatched);
    const bHaveUnMatched = b.products?.some(product => !product.unitMatched);

    if (aHaveUnMatched && !bHaveUnMatched) return -1;
    if (!aHaveUnMatched && bHaveUnMatched) return 1;
    return 0;
  });

  return sortedByUnMatched;
};

export const isValidUnit = (unit, validUnits) =>
  !!validUnits?.find(
    validUnit => unit === validUnit.key || unit === validUnit.value
  );

export const checkMatchesDuplicates = (matches, entitiesToMatch) => {
  let canSubmit = true;

  const cwfIds = matches
    .filter(
      m =>
        entitiesToMatch.findIndex(
          entity => entity.externalId === m.vendorEntityId
        ) >= 0
    )
    .map(match => match.cwfEntityId);
  const uniqueCwfIds = [...new Set(cwfIds)];
  const checkDuplicates = uniqueCwfIds.map(value => [
    value,
    cwfIds.filter(id => id === value).length
  ]);

  checkDuplicates.forEach(check => {
    if (check[1] > 1) {
      canSubmit = false;
    }
  });

  return canSubmit;
};

// Check for duplicate company names in createNewRetailers
export const checkDuplicatedNamesInNewCompanies = (
  companies,
  createNewRetailers
) => {
  const normalizedCompanyNames = new Set(
    companies?.map(company => company.name?.toLowerCase()?.trim())
  );

  const duplicateCompanyNames = createNewRetailers?.filter(retailer => {
    const normalizedRetailerName = retailer.retailerName?.toLowerCase()?.trim();
    return normalizedCompanyNames.has(normalizedRetailerName);
  });

  if (duplicateCompanyNames?.length > 0) {
    const duplicateNames = duplicateCompanyNames
      ?.map(r => r.retailerName)
      ?.join(', ');

    return duplicateNames;
  }
  return '';
};

export const displayErrorToast = (toast, title, content) => {
  toast.error(title, {
    supportButton: true,
    timeout: 30000,
    content
  });
};

export const createCompaniesInBatches = async (
  createCompanyApi,
  createNewRetailers
) => {
  // Process companies in batches of 3
  const batchSize = 3;
  const batches = Array.from(
    { length: Math.ceil(createNewRetailers.length / batchSize) },
    (_, i) => createNewRetailers.slice(i * batchSize, (i + 1) * batchSize)
  );

  const processBatch = batchIndex => {
    if (batchIndex >= batches.length) {
      return Promise.resolve([]);
    }

    const batch = batches[batchIndex];
    const batchPromises = batch.map(retailer =>
      createCompanyApi({
        name: retailer.retailerName.trim(),
        email: '',
        legalName: '',
        phone: '',
        address: {
          line1: '',
          line2: '',
          city: '',
          state: '',
          zipCode: '',
          country: ''
        },
        notes: 'Created from Import Invoice integration'
      }).then(response => ({
        retailer,
        company: response
      }))
    );

    return Promise.all(batchPromises).then(batchResults => {
      return processBatch(batchIndex + 1).then(remainingResults => [
        ...batchResults,
        ...remainingResults
      ]);
    });
  };

  const allBatchResults = await processBatch(0);

  return allBatchResults;
};
